import { LinearProgress, Stack } from '@mui/material';
import {
    DataGridPremium,
    GridPinnedColumns,
    GridColumnVisibilityModel,
    useGridApiRef,
} from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from 'shared/hooks';
import useManualSave from 'shared/hooks/useManualSave';
import { DataGridProProps } from 'shared/types';
import { CustomToolbar } from './CustomToolbar';
import { DataGridProPinColumnsModal } from './DataGridProPinColumnsModal';
import { ITableDataPageConfigProps } from './TableDataPage';
import { dataGridStyle } from 'shared/constants';

type DataGridProWrapperProps<T> = {
    dataGridProProps: DataGridProProps<T>;
    getExportHandler: () => Promise<void>;
    isExportLoading: boolean;
    config?: ITableDataPageConfigProps;
    title?: string;
    customToolbarChildren?: React.ReactNode[];
    dataGridProManualSaveProps: {
        processRowUpdate: ((newRow: T, oldRow: T) => T | Promise<T>) | undefined;
    };
    isRowEditLoading: boolean;
    isChildrenInlineWithGridToolbarComponents?: boolean;
};

export function DataGridProWrapper<T extends { id: string }>({
    dataGridProProps,
    getExportHandler,
    isExportLoading,
    isRowEditLoading,
    config,
    title,
    customToolbarChildren,
    dataGridProManualSaveProps,
    isChildrenInlineWithGridToolbarComponents,
}: DataGridProWrapperProps<T>) {
    const defaultPinned = useMemo(() => {
        return config?.pinnedColumnsProps?.pinnedColumnsDefault || [];
    }, [config]);
    const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
    const [headerGroupedHeight, setHeaderGroupedHeight] = useState<string>('56px');
    const [pinnedColumns, setPinnedColumns] = useLocalStorage<GridPinnedColumns>(
        config?.pinnedColumnsProps?.pinnedColumnsStorage || 'datagridpincolumns',
        { left: defaultPinned }
    );

    const hasRunOnce = useRef(false);

    useEffect(() => {
        const setPinnedColumnsOnce = () => {
            if (!hasRunOnce.current) {
                const left = (pinnedColumns.left || []).filter((pinned) => !defaultPinned.includes(pinned));

                setPinnedColumns({
                    left: [...defaultPinned, ...left],
                });
                hasRunOnce.current = true;
            }
        };

        setPinnedColumnsOnce();
    }, [defaultPinned, pinnedColumns.left, setPinnedColumns]);

    const [pinColumnsModalOpen, setPinColumnsModalOpen] = useState<boolean>(false);

    const handlePinnedColumnsChange = useCallback(
        (updatedPinnedColumns: GridPinnedColumns) => {
            setPinnedColumns(updatedPinnedColumns);
        },
        [setPinnedColumns]
    );

    const handlePinColumnsModalClose = () => {
        setPinColumnsModalOpen(false);
    };

    const handlePinColumnsModalOpen = () => {
        setPinColumnsModalOpen(true);
    };

    const getColumnsToHandlePin = (field: string): string[] => {
        const columnSelected = dataGridProProps.columns.find((c) => c.field === field);
        const columnsToHandle = [field];
        if (
            columnSelected?.headerClassName &&
            columnSelected?.headerClassName?.toString().includes('lead-group-header')
        ) {
            const leadGroupClassCategory = columnSelected.headerClassName?.toString().split(' ')[
                columnSelected.headerClassName?.toString().split(' ').length - 1
            ];
            dataGridProProps.columns.forEach((c) => {
                if (c.headerClassName && c.headerClassName?.toString().includes('grouped-header')) {
                    const groupedClassCategory = c.headerClassName?.toString().split(' ')[
                        c.headerClassName?.toString().split(' ').length - 1
                    ];
                    if (groupedClassCategory === leadGroupClassCategory) {
                        columnsToHandle.push(c.field);
                    }
                }
            });
        }
        return columnsToHandle;
    };

    const pinColumn = (field: string) => {
        const leftPinnedColumns = pinnedColumns.left;
        if (!leftPinnedColumns?.some((p) => p === field)) {
            const columnsToPush = getColumnsToHandlePin(field);
            leftPinnedColumns?.push(...columnsToPush);
        } else {
            const columnsToSplice = getColumnsToHandlePin(field);
            columnsToSplice.forEach((c) =>
                leftPinnedColumns?.splice(
                    leftPinnedColumns?.findIndex((p) => p === c),
                    1
                )
            );
        }
        handlePinnedColumnsChange({
            left: leftPinnedColumns,
        });
    };

    useEffect(() => {
        setHeaderGroupedHeight(
            dataGridProProps.columns.some(
                (c) => c.headerClassName && c.headerClassName?.toString().includes('grouped-header')
            )
                ? '80px'
                : '56px'
        );
    }, [dataGridProProps, setHeaderGroupedHeight]);

    const manualSave = useManualSave({
        isEnabled: !config?.manualSaveProps?.hideManualSave,
        handleIsEditableRow: config?.manualSaveProps?.handleIsEditableRow,
        columnConfig: dataGridProProps.columns,
        editButtonText: config?.manualSaveProps?.editButtonText,
    });

    const apiRef = useGridApiRef();

    const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(
        () =>
            dataGridProProps.columns.reduce<GridColumnVisibilityModel>((model, column) => {
                if (column.hide) model[column.field] = false;
                return model;
            }, {}),
        [dataGridProProps.columns]
    );

    // `initialState` only applies on mount, so columns whose `hide` depends on state that
    // resolves after mount (e.g. permissions) need to be re-applied once that state settles.
    useEffect(() => {
        apiRef.current?.setColumnVisibilityModel?.(columnVisibilityModel);
    }, [apiRef, columnVisibilityModel]);

    return (
        <>
            {config?.pinnedColumnsProps?.pinnable && (
                <DataGridProPinColumnsModal
                    columns={dataGridProProps.columns}
                    pinColumnsModalOpen={pinColumnsModalOpen}
                    pinnedColumns={pinnedColumns}
                    handlePinColumnsModalClose={handlePinColumnsModalClose}
                    pinColumn={pinColumn}
                    pinAllColumns={config.pinnedColumnsProps?.pinAllColumns}
                />
            )}
            <DataGridPremium
                apiRef={apiRef}
                sx={() => dataGridStyle(headerGroupedHeight)}
                autoHeight
                editMode={!config?.manualSaveProps ? 'row' : 'cell'}
                getRowId={(row) => row.id}
                {...dataGridProProps}
                initialState={{
                    pagination: {
                        paginationModel: {
                            page: 0,
                            pageSize: 15,
                        },
                    },
                    columns: {
                        columnVisibilityModel,
                    },
                    ...config?.initialState,
                }}
                filterMode='server'
                sortingMode='server'
                paginationMode='server'
                pageSizeOptions={[15, 25, 50, 100]}
                pagination
                pinnedColumns={pinnedColumns}
                onPinnedColumnsChange={handlePinnedColumnsChange}
                onCellKeyDown={manualSave?.handleCellKeyDown}
                cellModesModel={manualSave?.cellModesModel}
                {...(config?.manualSaveProps && !config?.manualSaveProps.hideManualSave === true
                    ? dataGridProManualSaveProps
                    : {})}
                components={{
                    Toolbar: CustomToolbar,
                    NoRowsOverlay: () => (
                        <Stack height='100%' alignItems='center' justifyContent='center'>
                            No Results
                        </Stack>
                    ),
                    LoadingOverlay: LinearProgress,
                }}
                componentsProps={{
                    panel: {
                        anchorEl: filterButtonEl,
                        sx: {
                            '& .MuiDataGrid-filterFormColumnInput': {
                                width: 'auto',
                            },
                        },
                    },
                    toolbar: {
                        hideExport: config?.hideExport,
                        setFilterButtonEl,
                        getExportHandler,
                        isExportLoading,
                        title,
                        manualSaveConfigProps: {
                            ...manualSave,
                            ...config?.manualSaveProps,
                            isRowEditLoading,
                        },
                        pinnedColumnsProps: {
                            ...config?.pinnedColumnsProps,
                            handlePinColumnsModalOpen,
                        },
                        children: customToolbarChildren,
                        isChildrenInlineWithGridToolbarComponents,
                    },
                    cell: {
                        onFocus: manualSave?.handleCellFocus,
                    },
                }}
            />
        </>
    );
}
